import React from 'react';
import { Table, Tag, Button, Input, Space, Card, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { formatVnDateTime } from '../../utils/vnDateTime';
import type { AdminInvitation } from '../../types/adminInvitation';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

/**
 * Status badge labels (Vietnamese):
 * SENT/PENDING => "Đã gửi"
 * ACCEPTED => "Đã chấp nhận"
 * EXPIRED => "Hết hạn"
 * REVOKED => "Đã hủy"
 */
function getStatusBadge(status: string) {
  const s = (status || '').toUpperCase();
  const map: Record<string, { color: string; label: string }> = {
    PENDING: { color: 'blue', label: 'Đã gửi' },
    SENT: { color: 'blue', label: 'Đã gửi' },
    ACCEPTED: { color: 'green', label: 'Đã chấp nhận' },
    EXPIRED: { color: 'default', label: 'Hết hạn' },
    REVOKED: { color: 'red', label: 'Đã hủy' },
  };
  const { color, label } = map[s] || { color: 'default', label: status };
  return <Tag color={color}>{label}</Tag>;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '–';
  const formatted = formatVnDateTime(value);
  return formatted === '—' ? '–' : formatted;
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    support: 'Support',
  };
  return map[role] || role;
}

export interface InvitationsTableProps {
  data: AdminInvitation[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  search: string;
  onSearchChange: (v: string) => void;
  onPageChange: (page: number) => void;
  onSearch: () => void;
  onResend: (record: AdminInvitation) => void;
  onRevoke: (record: AdminInvitation) => void;
  onInviteClick: () => void;
  resendingId: string | null;
  revokingId: string | null;
}

export const InvitationsTable: React.FC<InvitationsTableProps> = ({
  data,
  loading,
  total,
  page,
  limit,
  search,
  onSearchChange,
  onPageChange,
  onSearch,
  onResend,
  onRevoke,
  onInviteClick,
  resendingId,
  revokingId,
}) => {
  /** Resend/Revoke enabled for SENT, PENDING, EXPIRED. Disabled for ACCEPTED, REVOKED. */
  const canResendOrRevoke = (record: AdminInvitation) => {
    const status = (record.status || '').toUpperCase();
    return ['PENDING', 'SENT', 'EXPIRED'].includes(status);
  };

  const columns: ColumnsType<AdminInvitation> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <span style={{ fontWeight: 500, color: '#111827' }}>
          {email || '–'}
        </span>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (role: string) => <Tag color="blue">{roleLabel(role)}</Tag>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => getStatusBadge(status),
    },
    {
      title: 'Người mời',
      key: 'invitedBy',
      width: 140,
      render: () => '–',
    },
    {
      title: 'Gửi lúc',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 160,
      render: (v: string | null) => (
        <span style={{ color: '#6b7280' }}>{formatDate(v)}</span>
      ),
    },
    {
      title: 'Hết hạn vào',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 180,
      render: (v: string | null | undefined) => (
        <span style={{ color: '#6b7280' }}>{formatDate(v)}</span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: AdminInvitation) => {
        const canAct = canResendOrRevoke(record);
        return (
          <Space size="small">
            {canAct && (
              <>
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={resendingId === record.id}
                  onClick={() => onResend(record)}
                >
                  Gửi lại
                </Button>
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  loading={revokingId === record.id}
                  onClick={() => onRevoke(record)}
                >
                  Hủy
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  if (data.length === 0 && !loading) {
    return (
      <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 40 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Chưa có lời mời nào"
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onInviteClick}
          >
            Mời Admin
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 0 }}>
      {/* Filters */}
      <div
        style={{
          padding: 16,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Input
          placeholder="Tìm theo email..."
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          onPressEnter={onSearch}
          allowClear
          style={{ width: 240, borderRadius: 8 }}
        />
        <Button icon={<SearchOutlined />} onClick={onSearch}>
          Tìm kiếm
        </Button>
        <div style={{ flex: 1, minWidth: 0 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={onInviteClick}>
          Mời Admin
        </Button>
      </div>

      <Table<AdminInvitation>
        loading={loading}
        dataSource={data}
        rowKey="id"
        pagination={false}
        size="middle"
        columns={columns}
        style={{ borderRadius: 16, overflow: 'hidden' }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <span style={{ fontSize: 14, color: '#6b7280' }}>
          Hiển thị {start} đến {end} trong tổng số {total} kết quả
        </span>
        <Space>
          <Button
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            Trước
          </Button>
          <Button
            disabled={page * limit >= total}
            onClick={() => onPageChange(page + 1)}
          >
            Sau
          </Button>
        </Space>
      </div>
    </Card>
  );
};
